/* For use in AE mapping and field mapping post-import */

import { computed, onMounted, ref, shallowRef, toValue, watch } from 'vue';

import { EXCLUDED_SYSTEM_COLUMNS } from '@/composables/useFieldMappingVisibility';
import {
  FILE_UPLOAD_DATA_SOURCE_TYPE,
  GOOGLE_SHEETS_DATA_SOURCE_TYPE,
} from '@/constants/data_sources';
import { useFeedsStore, useSourcesStore } from '@/stores';

import useOfflinePartner from './useOfflinePartner';

export default function useColumnMapping(sourceId, sourceOverride = null) {
  const sourcesStore = useSourcesStore();
  const feedsStore = useFeedsStore();
  const { offlineFeeds } = useOfflinePartner();

  const loading = ref(true);
  const source = shallowRef(null);
  const allColumns = ref([]);
  const unmappedColumns = ref([]);

  const feed = computed(() => {
    const feedId = source.value?.feed_id;
    if (!feedId) return null;
    return (
      offlineFeeds.value.find((offlineFeed) => offlineFeed.id === feedId) ??
      feedsStore.getFeedById(feedId)
    );
  });

  async function populate(override) {
    loading.value = true;
    if (override) {
      source.value = override;
      await feedsStore.readySync;
    } else {
      await Promise.all([sourcesStore.readySync, feedsStore.readySync]);
      source.value = sourcesStore.sources.find((s) => s.id === sourceId);
    }
    allColumns.value = unusedSourceColumns(source.value);
    unmappedColumns.value = unusedSourceColumns(source.value);
    loading.value = false;
  }

  if (sourceOverride) {
    watch(
      () => toValue(sourceOverride),
      (val) => {
        if (val) populate(val);
      },
      { immediate: true },
    );
  } else {
    onMounted(() => populate(null));
  }

  const isSourceType = (type) =>
    computed(() => feed.value?.integration.type === type);

  const isSourceGoogleSheet = isSourceType(GOOGLE_SHEETS_DATA_SOURCE_TYPE);
  const isSourceCsv = isSourceType(FILE_UPLOAD_DATA_SOURCE_TYPE);

  // Filters out invalid source columns and columns that aren't mapped yet
  function unusedSourceColumns(source) {
    const columns =
      source?.fields.reduce((acc, field) => {
        if (
          field.is_visible &&
          field.mdm_property === null &&
          !EXCLUDED_SYSTEM_COLUMNS.includes(field.column)
        ) {
          acc.push({ value: field.column, label: field.column, id: field.id });
        }
        return acc;
      }, []) ?? [];

    return columns;
  }
  return {
    unusedSourceColumns,
    isSourceGoogleSheet,
    isSourceCsv,
    loading,
    feed,
    source,
    allColumns,
    unmappedColumns,
  };
}
